import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import Alert, { AlertVariant } from "@galaxy-io/dls/feedback/Alert";
import Skeleton from "@galaxy-io/dls/feedback/Skeleton";
import { InputSize } from "@galaxy-io/dls/inputs/Input";
import Flex, { AlignItems, FlexDirection } from "@galaxy-io/dls/layout/Flex";
import Text, { TextSize, TextVariant } from "@galaxy-io/dls/text/Text";
import Widget from "@galaxy-io/dls/widget/Widget";
import type { RecordFilterGroup } from "@gtm/contracts/core/record-filter";
import {
  type SavedFilter,
  type SavedFilterKind,
  savedFilterInput,
} from "@gtm/contracts/core/saved-filters";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SelectField } from "@/components/workspace/select-field";
import { apiClient, unwrap } from "@/lib/api-client";

export function SavedFilters({
  kind,
  draft,
  valid,
  onLoad,
  onDelete,
}: {
  kind: SavedFilterKind;
  draft: RecordFilterGroup;
  valid: boolean;
  onLoad: (filter: RecordFilterGroup) => void;
  onDelete: (filter: SavedFilter) => void;
}) {
  const client = useQueryClient();
  const queryKey = ["saved-filters", kind];
  const saved = useQuery({
    queryKey,
    queryFn: ({ signal }) =>
      unwrap(apiClient().GET("/saved-filters", { params: { query: { kind } }, signal })),
    retry: false,
  });
  const [selected, setSelected] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const item = saved.data?.find((filter) => filter.id === selected);
  async function save() {
    setSaving(true);
    setError(undefined);
    try {
      const created = await unwrap(
        apiClient().POST("/saved-filters", {
          body: savedFilterInput.parse({ kind, name, filter: draft }),
        }),
      );
      client.setQueryData<SavedFilter[]>(queryKey, (items = []) => [...items, created]);
      setSelected(created.id);
      setName("");
      void client.invalidateQueries({ queryKey });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not save filter.");
    } finally {
      setSaving(false);
    }
  }
  return (
    <Widget header="Workspace presets" subheader="Save and reuse these conditions with your team.">
      <Flex direction={FlexDirection.COLUMN} gap={16} width="100%">
        {saved.error ? (
          <Flex direction={FlexDirection.COLUMN} gap={8}>
            <Alert variant={AlertVariant.ERROR}>Saved filters unavailable.</Alert>
            <Button
              label="Retry saved filters"
              variant={ButtonVariant.SECONDARY}
              size={ButtonSize.SMALL}
              onClick={() => void saved.refetch()}
            />
          </Flex>
        ) : saved.isPending ? (
          <Skeleton lines={2} aria-label="Loading saved filters" />
        ) : saved.data.length ? (
          <>
            <SelectField
              compact
              search
              width="100%"
              label="Saved filters"
              placeholder="Choose a saved filter"
              value={selected}
              options={saved.data.map((filter) => ({ value: filter.id, label: filter.name }))}
              onChange={(id) => {
                setSelected(id);
              }}
            />
            <Flex gap={8}>
              <Button
                label="Load filter"
                size={ButtonSize.SMALL}
                variant={ButtonVariant.SECONDARY}
                isDisabled={!item}
                onClick={() => {
                  if (item) onLoad(item.filter);
                }}
              />
              <Button
                label="Delete saved filter"
                size={ButtonSize.SMALL}
                variant={ButtonVariant.TERTIARY}
                isDisabled={!item}
                onClick={() => {
                  if (item) onDelete(item);
                }}
              />
            </Flex>
          </>
        ) : (
          <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
            No saved filters yet.
          </Text>
        )}
        <Flex direction={FlexDirection.COLUMN} gap={8}>
          <Input
            label="Filter name"
            placeholder="Name these conditions"
            size={InputSize.SMALL}
            value={name}
            onChange={setName}
            disabled={saving}
          />
          <Flex alignItems={AlignItems.START}>
            <Button
              label="Save filter"
              size={ButtonSize.SMALL}
              variant={ButtonVariant.SECONDARY}
              isLoading={saving}
              isDisabled={!valid || !name.trim()}
              onClick={() => void save()}
            />
          </Flex>
        </Flex>
        {error && <Alert variant={AlertVariant.ERROR}>{error}</Alert>}
      </Flex>
    </Widget>
  );
}
